import { AuthenticationGuard } from "@authentication/components/AuthenticationGuard";
import { UnAuthorized } from "@authentication/components/UnAuthorized";
import { ConfirmEmail } from "@authentication/pages";
import { Login } from "@authentication/pages/Login";
import { Signup } from "@authentication/pages/Signup";
import { fetchUserProfile } from "@authentication/state/actions";
import { NotFound } from "@components/HttpErrors";
import { invenioConfig } from "@config";
import { AuthenticationRoutes } from "@routes/authentication/urls";
import { BackOffice } from "@routes/backoffice";
import { BackOfficeRoutes } from "@routes/backoffice/backofficeUrls";
import { FrontSite } from "@routes/frontsite";
import PropTypes from "prop-types";
import React, { Component } from "react";
import { connect, Provider } from "react-redux";
import { Route, Switch } from "react-router-dom";
import { configureStore } from "./store";
import { MediaContextProvider } from "@components/Media";
import ForgotPassword from "@authentication/pages/Password/ForgotPassword";
import ResetPassword from "@authentication/pages/Password/ResetPassword";
import GuidedTour from "@components/GuidedTour/GuidedTour";
import { sessionManager } from "@authentication/services/SessionManager";
import { logout } from "@authentication/state/actions";

if (process.env.NODE_ENV === "development") {
  const originalConsoleWarn = console.warn;
  console.warn = function (...args) {
    const message = args[0];
    if (typeof message === "string") {
      if (
        message.includes("Received `false` for a non-boolean attribute") ||
        message.includes("componentWill") ||
        message.includes("end value has mixed support") ||
        message.includes("Do not use Array index in keys") ||
        message.includes("Must use destructuring") ||
        message.includes("Fragments should contain") ||
        message.includes("propType") ||
        message.includes("defaultProps")
      ) {
        return;
      }
    }
    originalConsoleWarn.apply(console, args);
  };
}

export const ILSStore = configureStore();

class FetchUserComponent extends Component {
  componentDidMount() {
    const { fetchUserProfile } = this.props;
    fetchUserProfile();

    // Setup session timeout listener
    this.setupSessionTimeout();
  }

  setupSessionTimeout = () => {
    window.addEventListener("sessionTimeout", this.handleSessionTimeout);
  };

  handleSessionTimeout = () => {
    const { logoutUser } = this.props;

    // Reset session manager
    sessionManager.resetSession();

    // Clear all storage
    sessionStorage.clear();

    // Call the proper logout action (clears backend session + Redux state)
    logoutUser();

    // Show alert
    alert("Your session has expired due to inactivity");

    setTimeout(() => {
      window.location.replace(AuthenticationRoutes.login);
    }, 100);
  };

  componentWillUnmount() {
    // Cleanup listener
    window.removeEventListener("sessionTimeout", this.handleSessionTimeout);
  }

  render() {
    const { children } = this.props;
    return children;
  }
}

FetchUserComponent.propTypes = {
  children: PropTypes.node.isRequired,
  fetchUserProfile: PropTypes.func.isRequired,
  logoutUser: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  authData: state.authenticationManagement,
});

const mapDispatchToProps = (dispatch) => ({
  fetchUserProfile: () => dispatch(fetchUserProfile()),
  logoutUser: () => dispatch(logout()),
});

const FetchUser = connect(
  mapStateToProps,
  mapDispatchToProps
)(FetchUserComponent);

export default class App extends Component {
  constructor(props) {
    super(props);
    invenioConfig.merge(props.config);
  }

  render() {
    return (
      <MediaContextProvider>
        <Provider store={ILSStore}>
          <FetchUser>
            <Switch>
              {/* Authentication Routes - Public */}
              <Route exact path={AuthenticationRoutes.login}>
                <Login />
              </Route>
              <Route exact path={AuthenticationRoutes.signup}>
                <Signup />
              </Route>
              <Route exact path={AuthenticationRoutes.forgotPassword}>
                <ForgotPassword />
              </Route>
              <Route exact path={AuthenticationRoutes.resetPassword}>
                <ResetPassword />
              </Route>

              <AuthenticationGuard
                path={AuthenticationRoutes.confirmEmail}
                authorizedComponent={ConfirmEmail}
              />
              <AuthenticationGuard
                path={BackOfficeRoutes.home}
                authorizedComponent={BackOffice}
                unAuthorizedComponent={UnAuthorized}
                roles={["admin", "librarian"]}
              />

              <FrontSite />

              <Route>
                <NotFound />
              </Route>
            </Switch>
            <GuidedTour />
          </FetchUser>
        </Provider>
      </MediaContextProvider>
    );
  }
}

App.propTypes = {
  config: PropTypes.object,
};

App.defaultProps = {
  config: {},
};
